import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import GeneralLoading from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";

const RoleCreationDialog = ({ userDetails, open, setOpen, onComplete }) => {
  const ApiClient = useApiClient();

  const [selectedMembership, setSelectedMembership] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load roles
  useEffect(() => {
    if (!open) return;

    const fetchAvailableRoles = async () => {
      setLoading(true);
      try {
        const data = await ApiClient.users.getAllAvailableRoles();
        setAvailableRoles(data || []);
      } catch (err) {
        console.error("Failed to load roles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableRoles();
  }, [open]);

  // Reset state on close/open cycle
  useEffect(() => {
    if (!open) {
      setSelectedMembership(null);
      setSelectedRole(null);
    }
  }, [open]);

  // Build unified memberships list
  const memberships = [
    ...(userDetails.schoolMemberships || []).map((m) => ({
      id: m.id,
      type: "school",
      label: m.school?.name || "Unknown school",
    })),
    ...(userDetails.schoolGroupMemberships || []).map((m) => ({
      id: m.id,
      type: "schoolGroup",
      label: m.schoolGroup?.name || "Unknown group",
    })),
  ];

  const handleCreateRoleAssignment = async () => {
    if (!selectedMembership || !selectedRole) return;

    try {
      const result = await ApiClient.users.createRoleAssignment({
        role: selectedRole.id,
        schoolMembership:
          selectedMembership.type === "school" ? selectedMembership.id : null,
        schoolGroupMembership:
          selectedMembership.type === "schoolGroup"
            ? selectedMembership.id
            : null,
      });

      if (result.success) {
        enqueueSnackbar("Successfully added role assignment.", {
          variant: "success",
          autoHideDuration: 3000,
        });

        onComplete?.(result.roleAssignment);
        setOpen(false);
      } else {
        enqueueSnackbar(result.message || "Failed to add role assignment", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error adding role assignment:", error);
      enqueueSnackbar("Error adding role assignment. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleClose = () => setOpen(false);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>Add new role</DialogTitle>

      <DialogContent>
        {loading ? (
          <GeneralLoading />
        ) : (
          <>
            <Autocomplete
              options={memberships}
              value={selectedMembership}
              onChange={(_, value) => setSelectedMembership(value)}
              getOptionLabel={(option) =>
                `${option.label} (${
                  option.type === "school" ? "School" : "Group"
                })`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select membership"
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Autocomplete
              options={availableRoles}
              value={selectedRole}
              onChange={(_, value) => setSelectedRole(value)}
              getOptionLabel={(role) => role.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select role"
                  fullWidth
                  margin="normal"
                />
              )}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>

        <Button
          onClick={handleCreateRoleAssignment}
          variant="contained"
          disabled={!selectedMembership || !selectedRole}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { RoleCreationDialog };
